import moment from 'moment';
import 'moment-timezone';
import typeOf from './typeOf';
import runtimeConfig from '../config/runtimeConfig';
import UserStore from '../stores/UserStore';

const paramKeys = {
    createStartDate: 'created_at_from',
    createEndDate: 'created_at_to',
    createTimezone: 'created_at_timezone',
    updatedStartDate: 'updated_at_from',
    updatedEndDate: 'updated_at_to',
    updatedTimezone: 'updated_at_timezone',
    trackCreatedStartDate: 'track_created_at_from',
    trackCreatedEndDate: 'track_created_at_to',
    trackCreatedTimezone: 'track_created_at_timezone',
    conversionCreatedStartDate: 'conversion_created_at_from',
    conversionCreatedEndDate: 'conversion_created_at_to',
    conversionCreatedTimezone: 'conversion_created_at_timezone',
    conversionUpdatedStartDate: 'conversion_updated_at_from',
    conversionUpdatedEndDate: 'conversion_updated_at_to',
    conversionUpdatedTimezone: 'conversion_updated_at_timezone',
    actionTypes: 'action_types',
    actionNames: 'action_names',
    utmMedium: 'utm_medium',
    utmSource: 'utm_source',
    utmCampaign: 'utm_campaign',
    utmTerm: 'utm_term',
    utmContent: 'utm_content',
    advCids: 'adv_cids',
    qs: 'qs',
    offer: 'offer_ids',
    goals: 'goal_ids',
    goalActions: 'goal_action_ids',
    selfieLink: 'selfie_link',
    mimeTypes: 'mime_types',
    postbackIds: 'postback_ids',
    conversionStatuses: 'conversion_statuses',
    currentConversionStatuses: 'current_conversion_statuses',
    conversions: 'conversion_ids',
    accessGranted: 'access_granted',
    offerLocales: 'offer_locales',
    onlyForTrafficInPeriod: 'only_for_traffic_in_period',
    trackIds: 'track_ids',
    conversionIds: 'conversion_ids',
};
const arrayKeys = ['statusesSelectValue','localesSelectValue','accessSelectValue','actionTypes','actionNames','statuses','goals','goalActions','conversionStatuses','currentConversionStatuses','postbackIds','offer','conversions','accessGranted','offerLocales','trackIds','conversionIds'];
const jsonKeys = ['utmMedium','utmSource','utmCampaign','utmTerm','utmContent','param1','param2','param3','param4','advCids','qs'];
const exclude = ['metrics'];
const commonArrayKeys = arrayKeys.concat(jsonKeys);
const getTimezone = () => UserStore.getValidTimeZone();
// the format of the period bounds in the API requests (see parseToSend)
const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

const query = {
    parse(inputQuery) {
        const timezone = getTimezone();
        const query = (inputQuery && typeof inputQuery === 'object') ? { ...inputQuery } : {};

        for (let key in query) {
            let value = query[key];

            if (exclude.includes(key)) {
                continue;
            }

            if (commonArrayKeys.indexOf(key) !== -1) {
                if (typeOf.isString(value)) {
                    query[key] = [{ value, title: value }];
                } else if (Array.isArray(value)) {
                    query[key] = value.map(item => ({ value: item, title: item }));
                } else if (value && typeOf.isObject(value)) {
                    // leave as is if already in object form
                } else {
                    delete query[key];
                }
            } else if (typeOf.isNumeric(value)) {
                query[key] = value && +value || null;
            } else if (typeOf.isString(value) && value.match(/^\d{4}-\d{2}-\d{2}$/i)) {
                query[key] = moment.tz(value, runtimeConfig.codeFormat, timezone);
            } else if (!value) {
                delete query[key];
            }
        }

        let segments = null;
        if (query.segments) {
            try {
                segments = typeof query.segments === 'string' ? JSON.parse(query.segments) : query.segments;
            } catch (e) {
                segments = null;
            }
        }

        return {
            ...query,
            segments
        };
    },

    replace(query) {
        const targetPath = window.location.pathname;
        const targetHash = window.location.hash;
        const data = this.stringify({ ...query });

        const buildSearch = () => {
            const sp = new URLSearchParams();
            const keys = Object.keys(data).sort();
            keys.forEach((key) => {
                const value = data[key];
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        value.forEach((v) => sp.append(key, v));
                    } else {
                        sp.set(key, value);
                    }
                }
            });
            return sp.toString();
        };

        const applyReplace = () => {
            const currentPath = window.location.pathname;
            const finalPath = currentPath === targetPath ? targetPath : currentPath;
            const search = buildSearch();
            const currentSearch = window.location.search.replace(/^\?/, '');
            if (currentSearch === search) {
                return; // no-op to avoid redundant history updates causing re-renders
            }
            const newUrl = `${finalPath}${search ? `?${search}` : ''}${targetHash || ''}`;
            window.history.replaceState(null, '', newUrl);
        };

        // Defer to let router finish pushing the new path if navigation just happened
        setTimeout(applyReplace, 0);
    },

    /**
     * Parse a URL search string (e.g. "?a=1&b=2&b=3") into a plain object.
     * Repeated keys are collected into arrays. Mirrors the parsing done in RouterSync
     * so that `location.query` is available for legacy components under react-router v6.
     */
    parseSearch(search) {
        if (!search) return {};
        const queryParams = new URLSearchParams(search);
        const query = {};
        for (const [key, value] of queryParams.entries()) {
            if (query[key]) {
                if (Array.isArray(query[key])) {
                    query[key].push(value);
                } else {
                    query[key] = [query[key], value];
                }
            } else {
                query[key] = value;
            }
        }
        return query;
    },

    stringify(data) {
        for (let key in data) {
            let curValue = data[key];
            let value = null;

            if (moment.isMoment(curValue)) {
                value = curValue.format(runtimeConfig.codeFormat);
            } else if (typeOf.isArray(curValue)) {
                value = typeOf.isObject(curValue[0]) ? curValue.map(item => item.value) : curValue;
            } else if (typeOf.isObject(curValue)) {
                value = curValue.value;
            } else {
                value = curValue;
            }

            data[key] = value || undefined;
        }

        return data;
    },

    parseToSend(params = {}) {
        const timezone = getTimezone();
        let data = {
            count: runtimeConfig.itemsCountPerPage
        };

        for (let key in params) {
            if (!params[key]) continue;

            let value = params[key];
            let newKey = paramKeys[key] || key;

            if (moment.isMoment(value)) {
                // Dates are sent only as the wall-clock time 'YYYY-MM-DD HH:mm:ss' together with its timezone
                // (*_timezone, and `timezone` when it is not set) - no unix timestamps.
                // A date without an API parameter (no paramKeys entry, e.g. startDate / endDate from an old URL)
                // is not sent: the API does not know it.
                if (!paramKeys[key]) {
                    continue;
                }

                // period bounds (*StartDate / *EndDate): the start of the first day and the end (23:59:59, inclusive)
                // of the last one, in the field timezone (`${key}Timezone`, otherwise the user's)
                const baseKey = key.replace(/(Start|End)Date$/, '');
                const isStart = key.endsWith('StartDate');
                const isEnd = key.endsWith('EndDate');
                let dateTimezone;

                if (isStart || isEnd) {
                    dateTimezone = params[`${baseKey}Timezone`] || timezone;
                    const bound = moment.tz(value.format(runtimeConfig.codeFormat), runtimeConfig.codeFormat, dateTimezone);
                    data[newKey] = (isStart ? bound.startOf('day') : bound.endOf('day')).format(dateTimeFormat);
                } else {
                    dateTimezone = value.tz() || timezone;
                    data[newKey] = value.clone().tz(dateTimezone).format(dateTimeFormat);
                }

                const timezoneKey = paramKeys[`${baseKey}Timezone`];
                if (timezoneKey && !data[timezoneKey]) {
                    data[timezoneKey] = dateTimezone;
                }
                if (!params.timezone && !data.timezone) {
                    data.timezone = dateTimezone;
                }
            } else if (jsonKeys.indexOf(key) !== -1 && value.length) {
                data[newKey] = JSON.stringify(value.map(item => item.value));
            } else if (typeOf.isArray(value)) {
                data[newKey] = typeOf.isObject(value[0]) ? value.map(item => item.value).join(',') : value.join(',');
            } else {
                data[newKey] = value;
            }
        }

        return data;
    }
};

export default query;
